import {
  Box,
  Button,
  Flex,
  Image,
  Skeleton,
  Text,
  Tooltip,
  useMediaQuery,
  useOutsideClick,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import Expand from 'assets/expand_icon.png';
import HelpComponent from 'components/CustomHelp';
import ArrowLeftBlocks from 'components/Icons/ArrowLeftBlocks';
import Cross from 'components/Icons/Cross';
import transformDimensions from 'connectors/transformDimensions';
import ErrorPage from 'pages/ErrorPage';
import { STATUS } from 'projectConstants';
import {
  fetchDimensionItems,
  fetchIndicatorDetail,
  fetchModelTime,
  geModelTimeStatus,
  getIndicatorDetail,
  getIndicatorDetailStatus,
  getModelTime,
  getTableShimmer,
} from 'redux/IndicatorPageSlice';
import { getCurrentDimensionIndex } from 'redux/InputPageSlice';
import {
  closeInputSection,
  fetchBlockDrivers,
  FetchPlangPageIndicatorTableData,
  getIndicatorTableData,
  getIndicatorTableDataStatus,
  getIndicatorTableDataError,
  getmultiDimFiltersItems,
  getSelectedDriver,
  onErrorShow,
  planPageShow,
  toggleExpand,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import QuestionMarkIcon from 'components/Icons/QuestionMarkIcon';
import { AppDispatch } from 'utils/GlobalHelpers';
import ErrorBoundary from 'components/ErrorBoundary';

import Chart from './Chart';
import IndicatorTable from './IndicatorTable';
import InputTypeGroupedButton from './InputTypeGroupedButton';
import MultiDimSetting from './MultiDimSetting';

export interface Properties {
  refetchRequest: () => void;
}

const IndicatorPageMultiDim = ({ refetchRequest }: Properties) => {
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const containerReference = useRef<HTMLDivElement>(null);

  const [innerHeight, setInnerHeight] = useState(0);
  const [noDimChartData, setNoDimChartData] = useState<Record<string, Record<string, number>> | []>(
    [],
  );
  const currentDriver = useSelector(getSelectedDriver);
  const multiDimFiltersItems = useSelector(getmultiDimFiltersItems);

  const indicator = useSelector(getIndicatorDetail);
  const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
  const tabShimmer = useSelector(getTableShimmer);
  const modelTimeStatus = useSelector(geModelTimeStatus);
  const modelTime = useSelector(getModelTime);
  const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const showPlanPage = useSelector(planPageShow);
  const baseScenario = useSelector(getBaseScenario);
  const tableData = useSelector(getIndicatorTableData);
  const tableDataStatus = useSelector(getIndicatorTableDataStatus);
  const tableDataError = useSelector(getIndicatorTableDataError);

  const [isMobile] = useMediaQuery('(max-width: 1024px)');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const dimensionId = currentDriver?.dimensions[currentDimensionIndex];
  const tooltipReference = useRef<any>(null);

  useOutsideClick({
    ref: tooltipReference,

    handler: () => {
      setIsTooltipOpen(false);
    },
  });

  useLayoutEffect(() => {
    if (containerReference?.current) {
      const containerHeight = containerReference.current.offsetHeight;
      const containerPadding =
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
      const heightWithoutPadding = containerHeight - containerPadding;
      setInnerHeight(heightWithoutPadding);
    }
  }, [containerReference?.current]);

  const fetchModelTimeData = () => dispatch(fetchModelTime(Number(modelId)));

  const fetchDimItemsData = () => {
    dispatch(
      fetchDimensionItems({
        dimensionId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  useEffect(() => {
    if (modelId && blockId) {
      fetchModelTimeData();
    }
  }, [pathname, currentDriver]);

  useEffect(() => {
    if (currentDriver && currentDriver?.dimensions?.length > 0) {
      fetchDimItemsData();
    }
  }, [pathname, currentDriver?.id, baseScenario?.id, dimensionId]);

  useEffect(() => {
    if (currentDriver && baseScenario) {
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: currentDriver?.id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      );
    }
  }, [currentDriver, baseScenario?.id]);

  useEffect(() => {
    if (
      modelId &&
      blockId &&
      multiDimFiltersItems?.length === 0 &&
      tableData &&
      tableData?.column_headers
    ) {
      const chartFilterDimId = tableData?.column_headers[0].is_dimension
        ? tableData?.column_headers[0].id
        : null;
      dispatch(
        fetchIndicatorDetail({
          currentDriver,
          parameters: {
            dim_id: chartFilterDimId,
            is_chart_show: true,
            scenario_id: baseScenario?.id,
          },
        }),
      );
    }
  }, [currentDriver, baseScenario?.id, tableData]);

  useEffect(() => {
    if (indicator && !indicator?.output?.dims) {
      const object: any = {};
      object[indicator.output?.name] = indicator.output?.total_values;
      setNoDimChartData(object);
    }
  }, [indicator]);

  useEffect(() => {
    if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
      dispatch(onErrorShow(true));
    }
  }, [indicatorDetailStatus, modelTimeStatus]);

  const toggle = () => dispatch(toggleExpand());

  const closeSection = () => {
    dispatch(closeInputSection());

    if (!showPlanPage) {
      toggle();
    }
  };

  const showShimmer = indicatorDetailStatus !== STATUS.SUCCESS || tabShimmer === true;
  if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
    return (
      <>
        {isMobile && (
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            p={4}
            backgroundColor={'transparent'}
          >
            <Flex onClick={closeSection} alignItems='center' cursor='pointer'>
              {isMobile && (
                <Box mr='6px' fontFamily={'regular_roboto'}>
                  <ArrowLeftBlocks />
                </Box>
              )}
              {isMobile ? (
                <Text size='assistant_md'>Inputs</Text>
              ) : (
                <Flex alignItems={'center'}>
                  <Text ml={3} size='assistant_md'>
                    {indicator?.output?.name}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Cross height={'25px'} width={'25px'} onClick={closeSection} />
          </Flex>
        )}
        <Flex alignItems={'center'} justifyContent={'center'} height={'inherit'}>
          <ErrorPage planError={true} />
        </Flex>
      </>
    );
  }

  const refetchDrivers = async () => {
    await dispatch(
      fetchBlockDrivers({
        blockId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  const refetchData = async () => {
    fetchModelTimeData();
    if (currentDriver && currentDriver.dimensions.length > 0) {
      fetchDimItemsData();
    }
    refetchRequest();
  };

  // Show ErrorBoundary fallback if there's an API error
  if (tableDataStatus === STATUS.FAILED) {
    return (
      <Flex direction='column' align='center' justify='center' h='100vh' p={6}>
        <Text fontSize='xl' fontWeight='bold' color='red.600' mb={4}>
          Failed to Load Indicator Data
        </Text>
        <Text fontSize='md' color='gray.600' textAlign='center' maxW='500px' mb={6}>
          {tableDataError || 'Unable to fetch indicator table data. Please try again.'}
        </Text>
        <Button
          size='md'
          colorScheme='blue'
          onClick={() => {
            if (currentDriver && baseScenario) {
              dispatch(
                FetchPlangPageIndicatorTableData({
                  indicator_id: currentDriver?.id,
                  parameters: {
                    scenario_id: baseScenario?.id,
                    format_excel_data: true,
                  },
                }),
              );
            }
          }}
        >
          Retry
        </Button>
      </Flex>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <Flex direction='column' align='center' justify='center' h='100vh' p={6}>
          <Text fontSize='xl' fontWeight='bold' color='red.600' mb={4}>
            Something went wrong with the Indicator Page
          </Text>
          <Text fontSize='md' color='gray.600' textAlign='center' maxW='500px' mb={6}>
            {tableDataError ||
              'We encountered an unexpected error while loading the indicator data. This might be due to a network issue or a temporary problem with the data source.'}
          </Text>
          <Button
            size='md'
            colorScheme='blue'
            onClick={() => {
              if (currentDriver && baseScenario) {
                dispatch(
                  FetchPlangPageIndicatorTableData({
                    indicator_id: currentDriver?.id,
                    parameters: {
                      scenario_id: baseScenario?.id,
                      format_excel_data: true,
                    },
                  }),
                );
              } else {
                window.location.reload();
              }
            }}
          >
            {tableDataError ? 'Retry' : 'Reload Page'}
          </Button>
        </Flex>
      }
    >
      <Flex
        display='flex'
        flexDirection='column'
        // overflowY='scroll'
        className='scroll'
        h={`100vh`}
        width='100%'
        p={2}
        pb={'0 !important'}
      >
        <Flex justifyContent={'space-between'} alignItems={'center'}>
          <Flex onClick={closeSection} alignItems='center' cursor='pointer'>
            {isMobile && (
              <Box mr='6px' fontFamily={'regular_roboto'}>
                <ArrowLeftBlocks />
              </Box>
            )}
            {isMobile ? (
              <Text size='assistant_md'>Inputs</Text>
            ) : (
              <Flex
                alignItems={'center'}
                gap={'2'}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <Text ml={3} size='assistant_md'>
                  {indicator?.output?.name}
                </Text>
                {indicator?.help && (
                  <Tooltip
                    isOpen={isTooltipOpen}
                    hasArrow
                    arrowSize={10}
                    bg='white'
                    placement='right'
                    boxShadow={'rgba(0, 0, 0, 0.24) 0px 3px 8px'}
                    label={
                      <VStack
                        bg={'white'}
                        h='inherit'
                        p='3'
                        justify='space-between'
                        maxWidth={'300px'}
                        width={'180px'}
                        alignItems={'center'}
                        alignContent={'center'}
                        gap={1}
                        zIndex={1}
                      >
                        <Text
                          mb='2.5'
                          fontFamily='bold_roboto'
                          fontSize='sm'
                          lineHeight='4'
                          noOfLines={2}
                          color='black'
                        >
                          Help
                        </Text>
                        <Text
                          size='modalContent'
                          fontFamily='regular_roboto'
                          noOfLines={3}
                          color='black'
                          fontSize='2xs'
                        >
                          {indicator?.help}
                        </Text>
                      </VStack>
                    }
                    borderRadius='4'
                    width={'-moz-min-content'}
                  >
                    <Box
                      onMouseEnter={() => {
                        setIsTooltipOpen(true);
                      }}
                      onMouseLeave={() => {
                        setIsTooltipOpen(false);
                      }}
                      ref={tooltipReference}
                    >
                      <QuestionMarkIcon height={23} width={23} color='white' />
                    </Box>
                  </Tooltip>
                )}
              </Flex>
            )}
          </Flex>
          <Cross height={'25px'} width={'25px'} onClick={closeSection} />
        </Flex>
        {isMobile && (
          <Flex alignItems={'center'} mt={1}>
            <Text ml={3} size='assistant_md'>
              {indicator?.output?.name}
            </Text>
          </Flex>
        )}
        {showShimmer && (
          <Box
            h={innerHeight ? `${innerHeight}px` : '30vh'}
            pt={4}
            minH={innerHeight ? `${innerHeight}px` : '30vh'}
            mb='3'
          >
            <Skeleton startColor='white' endColor='#CCD4E8' h={'-webkit-fill-available'} />
          </Box>
        )}

        {!showShimmer && (
          <Box display={{ base: 'block' }} ref={containerReference} height={'25vh'}>
            {indicator?.output?.dims ? (
              <Chart
                showLabel={true}
                chartLabelData={indicator?.output.total_values}
                chartData={indicator?.output?.dim_item_data}
                chartType={indicator?.output?.chart_type}
                dimId={indicator?.output?.dims?.id}
                formatProperties={indicator?.output?.data_format_properties}
              />
            ) : (
              <Chart
                showLabel={true}
                chartLabelData={indicator?.output?.total_values}
                chartData={noDimChartData}
                chartType={indicator?.output?.chart_type}
                formatProperties={indicator?.output?.data_format_properties}
              />
            )}
          </Box>
        )}

        <Flex direction='column' borderTopLeftRadius='base' borderTopRightRadius='base'>
          {!showPlanPage && (
            <Flex alignItems={'center'} justifyContent={'space-between'} w={'100%'}>
              <Flex w={'100%'} justifyContent={'space-between'}>
                <Flex w={'60%'}>
                  <Box width={'70%'} maxWidth={'450px'} alignItems={'center'}>
                    {currentDriver.dimensions && (
                      <InputTypeGroupedButton
                        inputDimensions={transformDimensions(currentDriver.dimensions)}
                        blockId={currentDriver.block_id}
                        inputType={tableData?.type}
                        indicatorId={indicator?.id}
                        refetchDrivers={refetchDrivers}
                        inputDataValues={tableData?.data_values}
                        timeDimension={currentDriver.time_dim}
                        timeRange={modelTime?.time_range}
                        timeRangeYears={modelTime?.time_range_years}
                        refetchRequest={refetchData}
                      />
                    )}
                  </Box>
                  <Flex w={'30%'} align={'center'} ps={3}>
                    <MultiDimSetting indicatorId={indicator?.id} refetchRequest={refetchData} />
                  </Flex>
                </Flex>
                <VStack>
                  <Box
                    display={'flex'}
                    width='100%'
                    justifyContent={'end'}
                    alignItems={'center'}
                    cursor={'pointer'}
                    onClick={toggle}
                  >
                    <Text fontSize={'xs'}>Collapse view</Text>
                    <Image height={'1.5rem'} ml={1} src={Expand} />
                  </Box>
                  <Box>
                    <Text fontSize={'3xs'}>Click and drag cells to select multiple periods</Text>
                  </Box>
                </VStack>
              </Flex>
            </Flex>
          )}
          {showPlanPage && (
            <>
              <Box>
                {currentDriver?.dimensions && (
                  <InputTypeGroupedButton
                    inputDimensions={transformDimensions(currentDriver.dimensions)}
                    blockId={currentDriver.block_id}
                    inputType={tableData?.type}
                    indicatorId={indicator?.id}
                    refetchDrivers={refetchDrivers}
                    inputDataValues={tableData?.data_values}
                    timeDimension={currentDriver.time_dim}
                    timeRange={modelTime?.time_range}
                    timeRangeYears={modelTime?.time_range_years}
                    refetchRequest={refetchData}
                  />
                )}
              </Box>
              {isMobile && (
                <MultiDimSetting indicatorId={indicator?.id} refetchRequest={refetchData} />
              )}
              <Box
                display={{ base: 'none', md: 'flex' }}
                width='100%'
                justifyContent={'space-between'}
                alignItems={'center'}
                cursor={'pointer'}
              >
                <MultiDimSetting indicatorId={indicator?.id} refetchRequest={refetchData} />
                <Flex align={'center'}>
                  <Text onClick={toggle} fontSize={'xs'}>
                    Expand view
                  </Text>
                  <Image onClick={toggle} height={'1.5rem'} ml={1} src={Expand} />
                </Flex>
              </Box>
            </>
          )}

          {isMobile ? (
            <>
              <Box mt='1' mb='1' className='sm-scroll'>
                <IndicatorTable
                  refetchRequest={refetchData}
                  isPercentage={indicator?.data_format.toLowerCase()}
                />
              </Box>
              {indicator?.help && (
                <Box mt={5} pb={3}>
                  <HelpComponent helpText={indicator?.help} style={{ width: '100%' }} />
                </Box>
              )}
            </>
          ) : (
            <>
              <Box overflowY='scroll' className='scroll' maxH={'100%'}>
                <IndicatorTable
                  refetchRequest={refetchData}
                  isPercentage={indicator?.data_format.toLowerCase()}
                />
                {/* {indicator?.help && (
                <Box mt={5} pb={3}>
                  <HelpComponent
                    helpText={indicator?.help}
                    style={{ width: { base: '98%', '8xl': '380px' } }}
                  />
                </Box>
              )} */}
              </Box>
            </>
          )}
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

export default IndicatorPageMultiDim;
